import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import ProgressBarComponent from '@/app/shared/components/ProgressBar';
import { useCreditUtilizationWidget } from '@/app/shared/utils/hooks/useDebtorCreditUtilization';
import { CreditUtilizationSkeleton } from './CreditUtilizationSkeleton';
import { CreditUtilizationWidgetProps } from './CreditUtilization.types';
import {
  H2,
  Icon,
  IconNames,
  Paragraph,
  TextInput,
} from '@triumph/design-system';
import {
  WidgetContainer,
  WidgetHeader,
  HeaderRow,
  HeaderSubtitle,
  UtilizationSummary,
  LegendRow,
  LegendItem,
  LegendDot,
  CarriersTable,
  TableHeaderRow,
  TableRow,
  CarrierNameCell,
  UtilizationCell,
  EmptyState,
  SearchInputWrapper,
} from './CreditUtilizationWidget.StyledComponents';

export const CreditUtilizationWidget: React.FC<CreditUtilizationWidgetProps> = ({
  debtorId,
  companyId,
}) => {
  const { theme } = useAppContext();
  const { t } = useTranslation('invoicesDashboard');

  const { data, isLoading, isFetching, error, searchTerm, setSearchTerm } =
    useCreditUtilizationWidget({
      debtorId,
      companyId,
      enabled: !!debtorId && !!companyId,
    });

  if (isLoading) {
    return <CreditUtilizationSkeleton />;
  }

  if (!data) {
    return null;
  }

  const usedColor = theme.colors.semantic.warningDefault;
  const availableColor = theme.colors.greyscale.gray15;

  return (
    <WidgetContainer data-testid="credit-utilization-widget">
      <WidgetHeader>
        <HeaderRow>
          <Icon
            name={IconNames.Currency_Card}
            size="md"
            color={theme.colors.greyscale.gray50}
            id="credit-utilization-icon"
          />
          <H2>
            <b>{t('creditUtilization.title', 'Credit Utilization Overview')}</b>
          </H2>
        </HeaderRow>
        <HeaderSubtitle>
          <Paragraph small>
            {t(
              'creditUtilization.subtitle',
              'Timely payments can raise your available credit.',
            )}
          </Paragraph>
        </HeaderSubtitle>
      </WidgetHeader>
      <UtilizationSummary>
        <H2>
          <b>
            {t('creditUtilization.totalUtilized', {
              defaultValue: '{{percentage}}% Total Credit Utilized',
              percentage: data.usagePercentage,
            })}
          </b>
        </H2>
        <ProgressBarComponent count={data.usagePercentage} maxCount={100} />
        <LegendRow>
          <LegendItem>
            <LegendDot color={usedColor} />
            <Paragraph small>
              {t('creditUtilization.legend.used', 'Credit Used')}
            </Paragraph>
          </LegendItem>
          <LegendItem>
            <LegendDot color={availableColor} />
            <Paragraph small>
              {t('creditUtilization.legend.available', 'Credit Available')}
            </Paragraph>
          </LegendItem>
        </LegendRow>
      </UtilizationSummary>
      <SearchInputWrapper>
        <Icon
          name={IconNames.Interface_Search}
          size="sm"
          color={theme.colors.greyscale.gray50}
          id="credit-utilization-search-icon"
        />
        <TextInput
          id="credit-utilization-search"
          data-testid="credit-utilization-search-input"
          label={t(
            'creditUtilization.searchPlaceholder',
            'Search by Customer, or MC Number',
          )}
          placeholder={t(
            'creditUtilization.searchPlaceholder',
            'Search by Customer, or MC Number',
          )}
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
        />
      </SearchInputWrapper>
      <CarriersTable data-testid="carriers-table" style={{ opacity: isFetching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        <TableHeaderRow>
          <Paragraph small bold>
            {t('creditUtilization.table.carrier', 'Carrier')}
          </Paragraph>
          <Paragraph small bold>
            {t('creditUtilization.table.carrierPct', 'Carrier %')}
          </Paragraph>
        </TableHeaderRow>
        {data.clients.length === 0 ? (
          <EmptyState data-testid="credit-utilization-empty">
            <Paragraph small>
              {searchTerm
                ? t(
                    'creditUtilization.noResults',
                    'No carriers match your search.',
                  )
                : t(
                    'creditUtilization.noCarriers',
                    'No carrier data available.',
                  )}
            </Paragraph>
          </EmptyState>
        ) : (
          data.clients.map((client) => (
            <TableRow
              key={client.clientId}
              data-testid={`carrier-row-${client.clientId}`}
            >
              <CarrierNameCell>
                <Paragraph small>{client.name}</Paragraph>
              </CarrierNameCell>
              <UtilizationCell>
                <Paragraph small bold>
                  {client.utilization}%
                </Paragraph>
              </UtilizationCell>
            </TableRow>
          ))
        )}
      </CarriersTable>
    </WidgetContainer>
  );
};